import { sendUsageEmail } from "../usageEmail";
import { notifyLibrarySave } from "../library/libraryNotifications";
import { matchesLibrarySave } from "../library/libraryState";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import Repository, { baseURL } from "../../Repo/Repository";
import { fetchAgentToken, getUser } from "../../dashboard/userSlice";

// ─── Error code → user-friendly message map ─────────────────────────────────
const ERROR_MESSAGES = {
  AUTH_MISSING: "Please log in to use the AI Agent.",
  AUTH_INVALID: "Your session is invalid. Please log in again.",
  AUTH_EXPIRED: "Your session has expired. Please refresh the page.",
  AUTH_INACTIVE: "Your account is inactive. Please verify your email.",
  USER_NOT_FOUND: "Account not found. Please sign up or log in.",
  SESSION_NOT_FOUND: "Conversation not found.",
  RATE_LIMITED: "You're sending too many requests. Please wait a moment.",
  CREDITS_INSUFFICIENT: "Not enough credits. Please upgrade your plan.",
  INVALID_INPUT: "Please enter a valid message.",
  SERVER_ERROR: "Something went wrong. Please try again.",
  EXPORT_FAILED: "Failed to export. Please try again.",
  SHARE_FAILED: "Failed to create share link. Please try again.",
};

/**
 * Extract user-friendly error message from API response.
 * Handles both old format (string detail) and new format ({code, message}).
 */
const getErrorMessage = (err) => {
  const detail = err?.response?.data?.detail;
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(item => item.msg || "Invalid request").join(" ");
  if (detail.code && ERROR_MESSAGES[detail.code]) return ERROR_MESSAGES[detail.code];
  return detail.message || "Something went wrong. Please try again.";
};

/**
 * Streaming failures surface as browser-level messages ("Failed to fetch").
 * Translate the common ones into something a person can act on.
 */
const friendlyStreamError = (message) => {
  const text = String(message || "");
  if (/failed to fetch|networkerror|network request failed|load failed/i.test(text)) {
    return "I couldn't reach DataLumio. Check your internet connection and try again.";
  }
  if (/stream ended without a response/i.test(text)) {
    return "The connection dropped before I could finish. Please try again.";
  }
  if (/timeout|timed out/i.test(text)) {
    return "That took longer than expected and timed out. Please try again, perhaps with a smaller request.";
  }
  return text || "Something went wrong. Please try again.";
};

/** Get auth headers with JWT token. */
const authHeaders = () => {
  const token = localStorage.getItem("agent_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Wrap an async thunk body with auto-retry on 401 (token refresh).
 * If the first call returns 401, re-fetch the agent token and retry once.
 */
const withAuthRetry = async (fn, dispatch) => {
  try {
    return await fn();
  } catch (err) {
    const code = err?.response?.data?.detail?.code;
    if (err?.response?.status === 401 && (code === "AUTH_EXPIRED" || code === "AUTH_INVALID" || code === "AUTH_MISSING")) {
      // Try refreshing the token once
      await dispatch(fetchAgentToken()).unwrap();
      return await fn();
    }
    throw err;
  }
};

// Fetch all sessions for the user
export const fetchSessions = createAsyncThunk(
  "agent/fetchSessions",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // Note: authHeaders() is called fresh each time so retry gets the new token
      const makeReq = () => Repository.get("/agent/sessions", { headers: authHeaders() });
      const res = await withAuthRetry(makeReq, dispatch);
      return res.data.sessions;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// Fetch messages for a specific session
export const fetchSessionMessages = createAsyncThunk(
  "agent/fetchSessionMessages",
  async (sessionId, { rejectWithValue, dispatch }) => {
    try {
      const makeReq = () => Repository.get(`/agent/sessions/${sessionId}`, { headers: authHeaders() });
      const res = await withAuthRetry(makeReq, dispatch);
      return { sessionId, messages: res.data.messages, title: res.data.title };
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// Send a message to the agent (non-streaming, used by floating agent)
export const sendMessage = createAsyncThunk(
  "agent/sendMessage",
  async ({ message, files, sessionId }, { rejectWithValue, dispatch }) => {
    try {
      const payload = { message, session_id: sessionId || null };
      if (files && files.length > 0) {
        payload.files = files;
      }
      const makeReq = () => Repository.post("/agent/chat", payload, { headers: authHeaders() });
      const res = await withAuthRetry(makeReq, dispatch);
      if (res.data.remaining_credits !== undefined) refreshCredits(dispatch);
      sendUsageEmail("AI Agent", res.data.credits_used);
      notifyLibrarySave(res.data.library);
      return res.data;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// Delete a session
export const deleteSession = createAsyncThunk(
  "agent/deleteSession",
  async (sessionId, { rejectWithValue, dispatch }) => {
    try {
      const makeReq = () => Repository.delete(`/agent/sessions/${sessionId}`, { headers: authHeaders() });
      await withAuthRetry(makeReq, dispatch);
      return sessionId;
    } catch (err) {
      return rejectWithValue(getErrorMessage(err));
    }
  }
);

// Save chat organization through the same authenticated API as chat history.
export const updateSession = createAsyncThunk(
  "agent/updateSession",
  async ({ sessionId, updates }, { rejectWithValue, dispatch, getState }) => {
    if (!getState().agent.sessions.some((session) => session.id === sessionId)) {
      return rejectWithValue("Conversation not found. Please refresh your history.");
    }
    try {
      const makeReq = () => Repository.patch(`/agent/sessions/${encodeURIComponent(sessionId)}`, updates, { headers: authHeaders() });
      const res = await withAuthRetry(makeReq, dispatch);
      return { sessionId, session: res.data.session };
    } catch (err) {
      return rejectWithValue(err?.response?.status === 405
        ? "Chat changes aren't available yet. Please try again later."
        : getErrorMessage(err));
    }
  }
);

const agentSlice = createSlice({
  name: "agent",
  initialState: {
    sessions: [],
    activeSessionId: null,
    messages: [],
    sessionsLoading: false,
    messagesLoading: false,
    sendingMessage: false,
    error: null,
    pendingAction: null,
    suggestions: [],
    thinkingSteps: [],
    streamingText: "",
    toolInputRequest: null,
  },
  reducers: {
    setActiveSession: (state, action) => {
      state.activeSessionId = action.payload;
      state.messages = [];
      state.pendingAction = null;
    },
    clearActiveSession: (state) => {
      state.activeSessionId = null;
      state.messages = [];
      state.pendingAction = null;
      state.suggestions = [];
    },
    addLocalMessage: (state, action) => {
      state.messages.push(action.payload);
      state.suggestions = [];
    },
    setPendingAction: (state, action) => {
      state.pendingAction = action.payload;
    },
    clearPendingAction: (state) => {
      state.pendingAction = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    addThinkingStep: (state, action) => {
      const text = typeof action.payload === "string" ? action.payload.trim() : "";
      if (!text) return;
      if (state.thinkingSteps[state.thinkingSteps.length - 1] === text) return;
      state.thinkingSteps.push(text);
    },
    clearThinkingSteps: (state) => {
      state.thinkingSteps = [];
    },
    appendStreamingText: (state, action) => {
      state.streamingText += action.payload;
    },
    clearStreamingText: (state) => {
      state.streamingText = "";
    },
    setToolInputRequest: (state, action) => {
      state.toolInputRequest = action.payload;
    },
    clearToolInputRequest: (state) => {
      state.toolInputRequest = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase("library/saveConfirmed", (state, action) => {
        for (const message of state.messages) {
          if (matchesLibrarySave(message.library, action)) message.library = action.payload;
        }
      })
      // Fetch sessions
      .addCase(fetchSessions.pending, (state) => { state.sessionsLoading = true; })
      .addCase(fetchSessions.fulfilled, (state, action) => {
        state.sessionsLoading = false;
        state.sessions = action.payload;
      })
      .addCase(fetchSessions.rejected, (state, action) => {
        state.sessionsLoading = false;
        state.error = action.payload;
      })
      // Fetch session messages
      .addCase(fetchSessionMessages.pending, (state) => { state.messagesLoading = true; })
      .addCase(fetchSessionMessages.fulfilled, (state, action) => {
        state.messagesLoading = false;
        state.activeSessionId = action.payload.sessionId;
        state.messages = action.payload.messages;
        const lastAssistant = [...action.payload.messages].reverse().find(m => m.role === "assistant");
        state.suggestions = lastAssistant?.suggestions || [];
      })
      .addCase(fetchSessionMessages.rejected, (state, action) => {
        state.messagesLoading = false;
        state.error = action.payload;
      })
      // Send message (non-streaming)
      .addCase(sendMessage.pending, (state) => { state.sendingMessage = true; state.error = null; })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.sendingMessage = false;
        const { session_id, response, action: pendingAction } = action.payload;
        if (!state.activeSessionId) {
          state.activeSessionId = session_id;
        }
        const existingSession = state.sessions.find(s => s.id === session_id);
        if (!existingSession) {
          state.sessions.unshift({
            id: session_id,
            title: action.payload.session_title || "New Chat",
            created_at: new Date().toISOString(),
          });
        }
        state.messages.push({
          role: "assistant",
          content: response,
          created_at: new Date().toISOString(),
          files: action.payload.files || null,
          credits_used: action.payload.credits_used || 0,
          preamble: action.payload.preamble || null,
          suggestions: action.payload.suggestions || null,
          chart_data: action.payload.chart_data || null,
          library: action.payload.library || null,
          tool_used: "tool_used" in action.payload ? action.payload.tool_used || null : undefined,
        });
        state.suggestions = action.payload.suggestions || [];
        if (pendingAction) {
          state.pendingAction = pendingAction;
        }
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.sendingMessage = false;
        state.error = action.payload;
        state.messages.push({
          role: "assistant",
          content: action.payload || "Something went wrong. Please try again.",
          isError: true,
          created_at: new Date().toISOString(),
        });
      })
      // Delete session
      .addCase(deleteSession.fulfilled, (state, action) => {
        state.sessions = state.sessions.filter(s => s.id !== action.payload);
        if (state.activeSessionId === action.payload) {
          state.activeSessionId = null;
          state.messages = [];
        }
      })
      .addCase(updateSession.fulfilled, (state, action) => {
        const session = state.sessions.find((item) => item.id === action.payload.sessionId);
        if (session) Object.assign(session, action.payload.session);
      })
      // Send message stream & submit tool input — shared matchers
      .addMatcher(
        (action) => action.type === "agent/sendMessageStream/pending" || action.type === "agent/submitToolInput/pending",
        (state) => {
          state.sendingMessage = true;
          state.error = null;
          state.thinkingSteps = [];
          state.streamingText = "";
        }
      )
      .addMatcher(
        (action) => action.type === "agent/sendMessageStream/fulfilled" || action.type === "agent/submitToolInput/fulfilled",
        (state, action) => {
          state.sendingMessage = false;
          const { session_id, response, action: pendingAction } = action.payload;
          if (!state.activeSessionId) {
            state.activeSessionId = session_id;
          }
          const existingSession = state.sessions.find(s => s.id === session_id);
          if (!existingSession) {
            state.sessions.unshift({
              id: session_id,
              title: action.payload.session_title || "New Chat",
              created_at: new Date().toISOString(),
            });
          }
          state.messages.push({
            role: "assistant",
            content: response,
            created_at: new Date().toISOString(),
            files: action.payload.files || null,
            credits_used: action.payload.credits_used || 0,
            preamble: action.payload.preamble || null,
            suggestions: action.payload.suggestions || null,
            chart_data: action.payload.chart_data || null,
            library: action.payload.library || null,
            tool_used: "tool_used" in action.payload ? action.payload.tool_used || null : undefined,
            thinkingSteps: [...state.thinkingSteps],
          });
          state.suggestions = action.payload.suggestions || [];
          state.thinkingSteps = [];
          state.streamingText = "";
          if (pendingAction) {
            state.pendingAction = pendingAction;
          }
        }
      )
      .addMatcher(
        (action) => action.type === "agent/sendMessageStream/rejected" || action.type === "agent/submitToolInput/rejected",
        (state, action) => {
          state.sendingMessage = false;
          state.thinkingSteps = [];
          state.streamingText = "";
          if (action.payload === "__stopped__") return;
          state.error = action.payload;
          state.messages.push({
            role: "assistant",
            content: action.payload || "Something went wrong. Please try again.",
            isError: true,
            created_at: new Date().toISOString(),
          });
        }
      )
      .addMatcher(
        (action) => action.type === "agent/sendMessageStream/stopped",
        (state) => {
          state.sendingMessage = false;
          state.thinkingSteps = [];
          state.streamingText = "";
        }
      );
  },
});

export const {
  setActiveSession,
  clearActiveSession,
  addLocalMessage,
  setPendingAction,
  clearPendingAction,
  clearError,
  addThinkingStep,
  clearThinkingSteps,
  appendStreamingText,
  clearStreamingText,
  setToolInputRequest,
  clearToolInputRequest,
} = agentSlice.actions;

// Abort controller for stopping active streams
let _streamAbortController = null;

export const stopStreaming = () => (dispatch) => {
  if (_streamAbortController) {
    _streamAbortController.abort();
    _streamAbortController = null;
  }
  dispatch(agentSlice.actions.clearThinkingSteps());
  dispatch(agentSlice.actions.clearStreamingText());
  dispatch({ type: "agent/sendMessageStream/stopped" });
};

// Coalesce streamed text into one store update per ~50ms. Dispatching every
// token re-rendered the whole chat dozens of times a second and froze the page
// for up to a second at a time while an answer streamed.
function createTextBatcher(dispatch) {
  let pending = "";
  let timer = null;
  const flush = () => {
    if (timer) { clearTimeout(timer); timer = null; }
    if (pending) {
      const text = pending;
      pending = "";
      dispatch(appendStreamingText(text));
    }
  };
  return {
    push(text) {
      pending += text;
      if (!timer) timer = setTimeout(flush, 50);
    },
    flush,
    // Drop queued text so nothing lands after the stream has been cleared.
    cancel() {
      if (timer) { clearTimeout(timer); timer = null; }
      pending = "";
    },
  };
}

// Credits live in several pools (credits, pro_credits, starter_credits) and the
// stream only reports one number, so reload the user for the real balance.
const refreshCredits = (dispatch) => dispatch(getUser());

// SSE streaming thunk — defined after slice so exported actions are available
export const sendMessageStream = createAsyncThunk(
  "agent/sendMessageStream",
  async ({ message, files, sessionId }, { dispatch, rejectWithValue }) => {
    const text = createTextBatcher(dispatch);
    try {
      const payload = { message, session_id: sessionId || null };
      if (files && files.length > 0) payload.files = files;

      dispatch(clearThinkingSteps());

      _streamAbortController = new AbortController();
      const token = localStorage.getItem("agent_token");

      const response = await fetch(`${baseURL}/agent/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: _streamAbortController.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(getErrorMessage({ response: { data: errData } }));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();

        for (const line of lines) {
          const dataLine = line.replace(/^data: /, "").trim();
          if (!dataLine) continue;

          try {
            const event = JSON.parse(dataLine);
            if (event.type === "text_delta") {
              text.push(event.content);
            } else if (event.type === "step") {
              dispatch(addThinkingStep(event.content));
            } else if (event.type === "need_input") {
              dispatch(agentSlice.actions.setToolInputRequest(event));
            } else if (event.type === "done") {
              text.flush();
              if (event.remaining_credits !== undefined) refreshCredits(dispatch);
              sendUsageEmail("AI Agent", event.credits_used);
              _streamAbortController = null;
              notifyLibrarySave(event.library);
              return event;
            } else if (event.type === "error") {
              throw new Error(event.content || "Stream error");
            }
          } catch (parseErr) {
            if (parseErr instanceof SyntaxError) continue;
            throw parseErr;
          }
        }
      }

      text.cancel();
      _streamAbortController = null;
      return rejectWithValue(friendlyStreamError("Stream ended without a response"));
    } catch (err) {
      text.cancel();
      _streamAbortController = null;
      if (err.name === "AbortError") {
        return rejectWithValue("__stopped__");
      }
      return rejectWithValue(friendlyStreamError(err.message || "Streaming failed"));
    }
  }
);

// Submit tool input (user filled the interactive popup) — reuses SSE streaming
export const submitToolInput = createAsyncThunk(
  "agent/submitToolInput",
  async ({ inputId, selections, sessionId }, { dispatch, rejectWithValue }) => {
    const text = createTextBatcher(dispatch);
    try {
      dispatch(clearThinkingSteps());
      dispatch(agentSlice.actions.clearToolInputRequest());

      _streamAbortController = new AbortController();
      const token = localStorage.getItem("agent_token");

      const payload = {
        message: "__tool_input_resume__",
        session_id: sessionId,
        tool_input_resume: {
          input_id: inputId,
          selections,
        },
      };

      const response = await fetch(`${baseURL}/agent/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
        signal: _streamAbortController.signal,
      });

      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(getErrorMessage({ response: { data: errData } }));
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();

        for (const line of lines) {
          const dataLine = line.replace(/^data: /, "").trim();
          if (!dataLine) continue;

          try {
            const event = JSON.parse(dataLine);
            if (event.type === "text_delta") {
              text.push(event.content);
            } else if (event.type === "step") {
              dispatch(addThinkingStep(event.content));
            } else if (event.type === "done") {
              text.flush();
              if (event.remaining_credits !== undefined) refreshCredits(dispatch);
              sendUsageEmail("AI Agent", event.credits_used);
              _streamAbortController = null;
              notifyLibrarySave(event.library);
              return event;
            } else if (event.type === "error") {
              throw new Error(event.content || "Stream error");
            }
          } catch (parseErr) {
            if (parseErr instanceof SyntaxError) continue;
            throw parseErr;
          }
        }
      }

      text.cancel();
      _streamAbortController = null;
      return rejectWithValue(friendlyStreamError("Stream ended without a response"));
    } catch (err) {
      text.cancel();
      _streamAbortController = null;
      if (err.name === "AbortError") return rejectWithValue("__stopped__");
      return rejectWithValue(friendlyStreamError(err.message || "Tool input submission failed"));
    }
  }
);

export default agentSlice.reducer;
